import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { projectsAPI, type Project } from "../../services/api";
import ProgramDetailPage from "./ProgramDetailPage";

export default function ProgramDetailRoute() {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();

  const [program, setProgram] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!programId) {
      setError("Invalid program ID.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchProgram = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await projectsAPI.get(programId);
        if (!cancelled) {
          if (data) {
            setProgram(data);
          } else {
            setError("Program not found.");
          }
        }
      } catch {
        if (!cancelled) {
          setError("Failed to load program details. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProgram();
    return () => {
      cancelled = true;
    };
  }, [programId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F0] flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A1E1E]" />
        <p className="text-zinc-500 text-sm font-medium">Loading program details…</p>
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="min-h-screen bg-[#FFF9F0] flex flex-col items-center justify-center gap-6 px-4">
        <p className="text-zinc-600 text-center text-base font-medium">
          {error ?? "Program not found."}
        </p>
        <button
          onClick={() => navigate("/programs")}
          className="inline-flex items-center gap-2 rounded-xl bg-[#7A1E1E] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#8B2E2E] transition-colors"
        >
          ← Back to Programs
        </button>
      </div>
    );
  }

  return (
    <ProgramDetailPage
      program={program}
      onBack={() => navigate("/programs")}
    />
  );
}
